import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import axios from "axios"
import _ from "lodash"
import { lang } from "config"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import SelectSearchable from "components/forms/select-searchable"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import useBranchRepositories from "repositories/branch"
import { getBranchList, getCurrentEntity, getGenericSupplier, getParameter } from "helpers/general-helper"
import { defaultUomForEntity, loadMasterUomFactors, masterUomFactor, normalizeUomCode } from "helpers/uom-helper"
import { Plus } from "components/icons/outline"
import PurchaseExportActions from "./export-actions"
import ProductPickerModal from "./product-picker-modal"

const PO_TYPE = "REGULAR"

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function moneyFmt(v) {
  const n = num(v)
  if (!n) return "-"
  return Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(n)
}

// 210726 (permintaan user): qty lusin/pasang pecahan SELALU tampil 2 desimal
// ("0,50", "1,17" — bukan "0,5"); bilangan bulat tetap tanpa koma ("2").
function qtyFmt(v) {
  const n = num(v)
  if (Number.isInteger(n)) return String(n)
  return n.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Varian utk <input type="number"> (titik desimal wajib): pecahan → "0.50", bulat → "2".
// Dipakai saat load & onBlur supaya tampilan input konsisten 2 desimal dgn print.
function qtyInputStr(v) {
  const n = Math.round(num(v) * 100) / 100
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

function pickCostPrice(p) {
  const candidates = [
    p?.price,
    p?.pcs_price,
    p?.maximum_retail_price,
    p?.purchase_price,
    p?.buy_price,
    p?.cost_price,
    p?.hpp,
    p?.cogs,
  ]
  for (const c of candidates) {
    const n = Number(c)
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

function pickProductLabel(p) {
  return p?.product_unit_long_name || p?.product_unit || p?.sku || p?.product_model || ""
}

function normalizeDateInput(value) {
  if (!value) return ""
  if (typeof value !== "string") return ""
  return value.slice(0, 10)
}

function fmtPrintDate(v) {
  if (!v) return "-"
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(d)
}

const PurchaseOrderPrintDocument = forwardRef(function PurchaseOrderPrintDocument(
  {
    poNumber,
    status,
    poType,
    outletName,
    supplierName,
    poDate,
    notes,
    items = [],
    totalQty = 0,
    totalAmount = 0,
    sellPriceTotal = 0,
    printedBy,
  },
  ref
) {
  const printedAt = new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date())

  const PAGE_SIZE = 20
  const chunks = []
  for (let i = 0; i < items.length; i += PAGE_SIZE) {
    chunks.push(items.slice(i, i + PAGE_SIZE))
  }
  if (chunks.length === 0) chunks.push([])

  const renderHeader = () => (
    <>
      <div className="flex items-start justify-between pb-4 border-b border-neutral-300">
        <div>
          <div className="text-xl font-bold tracking-wide">PURCHASE ORDER</div>
          <div className="text-[12px] text-neutral-600">Order Pembelian</div>
        </div>
        <div className="text-right">
          <div className="text-[13px] font-semibold">{poNumber || "-"}</div>
          <div className="text-[11px] text-neutral-600">
            Status: {String(status || "DRAFT").toUpperCase()}
            {poType ? ` • ${poType}` : ""}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 mt-4 gap-x-8 gap-y-1">
        <div className="flex">
          <div className="w-40 text-neutral-500">Tanggal PO</div>
          <div className="flex-1 font-medium">{fmtPrintDate(poDate)}</div>
        </div>
        <div className="flex">
          <div className="w-40 text-neutral-500">Outlet / Gudang</div>
          <div className="flex-1 font-medium">{outletName || "-"}</div>
        </div>
        <div className="flex col-span-2">
          <div className="w-40 text-neutral-500">Supplier</div>
          <div className="flex-1 font-medium">{supplierName || "-"}</div>
        </div>
      </div>
    </>
  )

  return (
    <div ref={ref} className="hidden print:block">
      <style>{`
        @page { size: A4; margin: 16mm 14mm; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .po-page { page-break-after: always; }
          .po-page:last-child { page-break-after: auto; }
        }
      `}</style>

      {chunks.map((chunkItems, pageIdx) => {
        const isLastPage = pageIdx === chunks.length - 1
        const baseIndex = pageIdx * PAGE_SIZE

        return (
          <div key={pageIdx} className="po-page text-[12px] text-neutral-900">
            {renderHeader()}

            <table className="w-full mt-5 border-collapse">
              <thead>
                <tr className="bg-neutral-100">
                  <th className="w-8 px-2 py-1 text-left border border-neutral-300">No</th>
                  <th className="px-2 py-1 text-left border border-neutral-300">Produk</th>
                  <th className="w-16 px-2 py-1 text-left border border-neutral-300">UOM</th>
                  <th className="w-20 px-2 py-1 text-right border border-neutral-300">Qty</th>
                  <th className="w-28 px-2 py-1 text-right border border-neutral-300">Cost Price</th>
                  <th className="w-32 px-2 py-1 text-right border border-neutral-300">Total</th>
                </tr>
              </thead>
              <tbody>
                {chunkItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-2 py-3 text-center border border-neutral-300 text-neutral-500">
                      Tidak ada item
                    </td>
                  </tr>
                )}
                {chunkItems.map((it, i) => {
                  const qty = Number(it.qty_ordered) || 0
                  const cost = Number(it.cost_price) || 0
                  return (
                    <tr key={it.product_unit_id || `${pageIdx}-${i}`}>
                      <td className="px-2 py-1 align-top border border-neutral-300">{baseIndex + i + 1}</td>
                      <td className="px-2 py-1 align-top border border-neutral-300">
                        <div>{it.product_label || it.product_unit_id}</div>
                        {it.product_label ? (
                          <div className="text-[10px] text-neutral-500">{it.product_unit_id}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1 align-top border border-neutral-300">{it.uom || "-"}</td>
                      <td className="px-2 py-1 text-right align-top border border-neutral-300">{qtyFmt(qty)}</td>
                      <td className="px-2 py-1 text-right align-top border border-neutral-300">
                        {moneyFmt(cost)}
                      </td>
                      <td className="px-2 py-1 text-right align-top border border-neutral-300">
                        {moneyFmt(qty * cost)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {isLastPage ? (
                <tfoot>
                  <tr className="font-semibold bg-neutral-50">
                    <td colSpan={3} className="px-2 py-1 text-right border border-neutral-300">
                      Total
                    </td>
                    <td className="px-2 py-1 text-right border border-neutral-300">{qtyFmt(totalQty)}</td>
                    <td className="px-2 py-1 border border-neutral-300" />
                    <td className="px-2 py-1 text-right border border-neutral-300">{moneyFmt(totalAmount)}</td>
                  </tr>
                  {sellPriceTotal ? (
                    <tr className="bg-neutral-50">
                      <td colSpan={5} className="px-2 py-1 text-right border border-neutral-300 text-neutral-600">
                        Sell Price Total (ref)
                      </td>
                      <td className="px-2 py-1 text-right border border-neutral-300 text-neutral-600">
                        {moneyFmt(sellPriceTotal)}
                      </td>
                    </tr>
                  ) : null}
                </tfoot>
              ) : (
                <tfoot>
                  <tr>
                    <td colSpan={6} className="px-2 py-1 text-right border border-neutral-300 text-[11px] text-neutral-500 italic">
                      bersambung ke halaman berikutnya…
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>

            {isLastPage && notes ? (
              <div className="mt-4">
                <div className="text-neutral-500">Catatan</div>
                <div className="mt-1 whitespace-pre-wrap border border-neutral-200 rounded p-2 min-h-[48px]">
                  {notes}
                </div>
              </div>
            ) : null}

            {isLastPage ? (
              <div className="grid grid-cols-3 gap-6 mt-12 text-center">
                {["Dibuat oleh", "Disetujui oleh", "Penerima"].map((role) => (
                  <div key={role}>
                    <div className="text-neutral-600">{role}</div>
                    <div className="h-16" />
                    <div className="pt-1">( _______________ )</div>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="mt-8 pt-2 border-t border-neutral-200 flex items-center justify-between text-[10px] text-neutral-400">
              <span>
                {poNumber ? `${poNumber} • ` : ""}Dicetak{printedBy ? ` oleh ${printedBy}` : ""} pada {printedAt}
              </span>
              <span>Halaman {pageIdx + 1} dari {chunks.length}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
})

// 200726 (PCS/LUSIN): qty & harga item PO TERSIMPAN SELALU PER PCS di server —
// pilihan LUSIN hanya cara input/tampilan (1 lusin = 12 pcs). Konversi dilakukan
// saat submit (payload) dan saat hydrate (edit).
// 210726: + PASANG (2 pcs).
// 230726 (permintaan user): opsi satuan diambil dari MASTER satuan (LSN/PSG/GRS/…);
// faktor pcs dari qty_per_unit master via helpers/uom-helper (fallback lama tetap).
const UOM_OPTIONS = ["PCS", "LUSIN", "PASANG"] // fallback saat master belum termuat
const uomFactor = masterUomFactor

// 250726 (fix): dozen_store_price = harga per LUSIN (12 pcs) — berlaku utk satuan
// ber-faktor 12 (kode MASTER "LSN"/"DGR", atau fallback lama "LUSIN"). Dulu cek
// hardcoded `=== "LUSIN"` sehingga saat SELECT memakai kode master "LSN", harga
// lusin TIDAK terpakai → jatuh ke pcs × 12 (mis. 114.000 padahal dozen 111.000).
const isDozenUom = (u) => {
  const uu = String(u || "").trim().toUpperCase()
  return uu === "LUSIN" || uu === "LSN" || uomFactor(u) === 12
}

export default function PurchaseOrderForm({ data, errors: externalErrors, onSubmit, submitting = false }) {
  const { id } = useParams()
  const isEdit = Boolean(id)

  const { can, currentBranch,user, canFilterAllOutlets } = useAuth()
  const { title, transition, setTransition } = useOutletContext()
  const location = useLocation()
  const navigate = useNavigate()

  const branchSelection = getBranchList()
  const supplierSelection = getGenericSupplier()

  // Admin HO (super ATAU node 'outlet.filter-entity') boleh membuat PO untuk outlet
  // LAIN. User toko biasa → picker dikunci ke outlet sendiri (read-only).
  // 200726 (keputusan user): khusus form PO, pembatasan ENTITAS pada daftar outlet
  // (restrictBranches/ATALLA-403) DIHILANGKAN — pemegang picker melihat SEMUA outlet
  // lintas entity (unrestricted, pola ATALLA-389). Gate siapa yang boleh memilih
  // (canPickOutlet) tetap berlaku.
  const canPickOutlet = canFilterAllOutlets()
  const { data: outletData } = useBranchRepositories({ paginate: false, unrestricted: true })
  const outletOptions = useMemo(() => {
    const rows = Array.isArray(outletData) ? outletData : outletData?.data ?? []
    // 230726 (rule entity): user VISION hanya boleh memilih outlet ber-is_ho
    // (HO/gudang) saat membuat PO; entity lain tetap bebas lintas entitas
    // (keputusan 200726). Backend memvalidasi hal yang sama.
    const filtered = getCurrentEntity() === "VISION" ? rows.filter((b) => !!b?.is_ho) : rows
    // 270726 (permintaan user): urutkan outlet ASC by id supaya posisinya tetap &
    // mudah dicari (urutan bawaan API mengikuti relevansi/nama).
    return [...filtered].sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0))
  }, [outletData])

  const [errors, setErrors] = useState(externalErrors || {})

  // Header state
  const [poId, setPoId] = useState(null)
  const [poNumber, setPoNumber] = useState(null)
  // 240726 (permintaan user): nomor DO ter-generate (dari cetak); tampil read-only.
  const [poDoNo, setPoDoNo] = useState(null)
  const [outletId, setOutletId] = useState(currentBranch?.id ?? null)
  const [outletName, setOutletName] = useState(currentBranch?.name ?? null)
  const [supplierId, setSupplierId] = useState(null)
  const [supplierName, setSupplierName] = useState(null)
  const [poSource, setPoSource] = useState("FACTORY")

  // 100726 (permintaan user): filter supplier per sumber DIHILANGKAN — dropdown
  // menampilkan SEMUA supplier apa pun sumber PO-nya (dulu: VISION → hanya supplier
  // internal Vision; FACTORY/IMPORT → hanya non-Vision, sehingga akun vision malah
  // melihat supplier atalla saja). Kombinasi sumber VISION + supplier non-internal
  // tetap ditolak backend dgn pesan jelas (validasi di PurchaseOrderController::store).
  // 270726 (permintaan user): daftar supplier diurutkan ASC by id supplier.
  const filteredSupplierSelection = useMemo(
    () =>
      (Array.isArray(supplierSelection) ? [...supplierSelection] : []).sort(
        (a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0)
      ),
    [supplierSelection]
  )
  const [poDate, setPoDate] = useState(todayISO())
  // Estimasi barang datang (opsional) — tampil di laporan Outstanding PO.
  const [estimateDate, setEstimateDate] = useState("")
  // 200726 (permintaan user): diskon level-PO — PERCENT (%) atau AMOUNT (Rp).
  const [discountType, setDiscountType] = useState("")
  const [discountValue, setDiscountValue] = useState("")
  const [notes, setNotes] = useState("")
  const [status, setStatus] = useState("DRAFT")
  // 210726 (permintaan user): satuan (PCS/LUSIN/PASANG) GLOBAL untuk seluruh PO —
  // bukan per item. Semua baris item memakai satuan ini; ganti satuan mengonversi
  // HPP semua baris sekaligus (server tetap menyimpan PCS).
  // 290726: default entitas diisi SINKRON saat mount (lihat ho-order/form).
  const [globalUom, setGlobalUom] = useState(() => defaultUomForEntity(getCurrentEntity(), null))

  // 230726 (permintaan user): opsi satuan dari MASTER satuan (GET /satuan).
  // null = belum termuat — hydrate item edit menunggu ini supaya konversi
  // tampilan (stored pcs ÷ faktor) memakai faktor master yang benar (mis. GRS=144).
  const [satuanOptions, setSatuanOptions] = useState(null)
  // 240726 (#3 permintaan user): pencarian item — 1 PO bisa banyak produk, jadi
  // daftar item bisa difilter per nama/kode (hanya menyaring tampilan, bukan state).
  const [itemSearch, setItemSearch] = useState("")
  useEffect(() => {
    let cancelled = false
    loadMasterUomFactors().then((names) => {
      if (cancelled) return
      const opts = names.length ? names : [...UOM_OPTIONS]
      setSatuanOptions(opts)
      // 290726 (permintaan user): satuan default ikut ENTITAS user (VISION→PASANG,
      // ATALLA→LUSIN). HANYA untuk PO baru — saat edit, satuan diambil dari data
      // tersimpan lewat hydrate di bawah supaya nilai lama tidak tertimpa.
      if (!data) setGlobalUom((prev) => normalizeUomCode(prev) || prev)
    })
    return () => { cancelled = true }
  }, [])

  const outletGroupId = useMemo(() => getParameter("OUTLET_GROUP_ID") ?? null, [])

  // Items state
  const [items, setItems] = useState([])

  // UI add item
  const [selectedProductUnitId, setSelectedProductUnitId] = useState("")
  const [addQty, setAddQty] = useState("")
  const [productSelectKey, setProductSelectKey] = useState(0)

  // SERVER SEARCH STATE
  const [productSearch, setProductSearch] = useState("") // debounced (triggers fetch)
  const [productSearchValue, setProductSearchValue] = useState("") // instant input value
  const [productPage, setProductPage] = useState(1)
  const perPage = 20

  const [selectedProductUnit, setSelectedProductUnit] = useState(null)

  const productCacheRef = useRef(new Map())

  // keep errors in sync from parent
  useEffect(() => {
    setErrors(externalErrors || {})
  }, [externalErrors])

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ✅ Hydrate from props.data (NO FETCH HERE)
  useEffect(() => {
    if (!data) return
    // 230726: tunggu faktor satuan master termuat dulu (konversi ÷faktor di bawah).
    if (satuanOptions === null) return

    // fleksibel: kalau loader bungkus di purchase_transaction
    const po = data?.purchase_transaction ?? data?.data ?? data

    setPoId(po?.id ?? null)
    setPoNumber(po?.po_number ?? po?.public_id ?? null)
    setPoDoNo(po?.do_no ?? po?.header?.do_no ?? null)

    const outId = po?.outlet_id ?? currentBranch?.id ?? null
    setOutletId(outId)

    const supId = po?.supplier_id ?? null
    setSupplierId(supId)
    console.log(po,"po_date")
    setPoDate(normalizeDateInput(po?.po_date) || todayISO())
    setEstimateDate(normalizeDateInput(po?.estimate_date) || "")
    setDiscountType(po?.discount_type ?? "")
    setDiscountValue(po?.discount_value != null && Number(po.discount_value) > 0 ? String(po.discount_value) : "")
    setNotes(po?.remarks ?? po?.notes ?? "")
    setStatus(po?.status ?? po?.latest_flow_detail_id ?? "DRAFT")
    setPoSource((po?.po_source ?? "FACTORY").toUpperCase())

    const b = branchSelection?.find((x) => String(x.id) === String(outId))
    setOutletName(b?.name ?? po?.outlet_name ?? null)

    const s = supplierSelection?.find((x) => String(x.id) === String(supId))
    setSupplierName(s?.name ?? po?.supplier_name_snapshot ?? po?.supplier_name ?? null)

    // items bisa dari beberapa key
    const rows =
      (Array.isArray(po?.items) && po.items) ||
      (Array.isArray(data?.purchase_item_side_by_side) && data.purchase_item_side_by_side) ||
      []

    // 210726: satuan GLOBAL — ambil dari item pertama (fallback PCS), lalu semua
    // baris ditampilkan dalam satuan itu (stored PCS ÷ faktor global).
    const firstUomRaw = String(rows[0]?.uom ?? "PCS").trim().toUpperCase()
    // 230726: terima satuan apa pun (master/legacy) — faktor dari uomFactor.
    // 290726: label lama ("LUSIN"/"PASANG") dipetakan ke kode master ("LSN"/"PSG")
    // supaya dropdown & modal Pilih Banyak menampilkan kosakata yang sama. Faktornya
    // identik, jadi qty/HPP PO lama tidak berubah.
    const gUom = normalizeUomCode(firstUomRaw) || "PCS"
    setGlobalUom(gUom)
    const f = uomFactor(gUom)
    // 200726 (PCS/LUSIN): tersimpan PCS — tampilkan kembali per uom pilihan.
    // 210726: bulatkan hasil ÷f ke 4 desimal agar 2,04÷12 tampil 0,17 (bukan
    // 0,16999… karena noise float), tapi tetap presisi utk lusin pecahan.
    // 210726 (permintaan user): qty LUSIN/PASANG maksimal 2 desimal — 1,92 bukan 1,9166…
    const toUom = (v) => Math.round((Number(v ?? 0) / f) * 100) / 100
    setItems(
      rows
        .map((r) => {
          return {
            id: r?.id,
            product_unit_id: r?.product_unit_id,
            product_label: r?.product_label ?? r?.product_name ?? r?.product_unit_name ?? r?.product_unit_id,
            // 240726 (#5): status "sisa ditutup" per item (dari backend).
            closed: !!Number(r?.closed ?? 0),
            uom: gUom,
            // 210726: string terformat ("0.50") supaya input tampil 2 desimal saat load.
            qty_ordered: qtyInputStr(toUom(r?.qty_ordered ?? r?.qty ?? 0)),
            qty_received: qtyInputStr(toUom(r?.qty_received ?? r?.qty ?? 0)),
            cost_price: Number(r?.cost_price ?? r?.price ?? 0) * f,
            // 290726 (laporan user): harga DASAR per PCS disimpan terpisah supaya
            // ganti satuan tidak menurunkan harga dari satuan sebelumnya. Dulu
            // konversi memakai (cost_price / faktor_lama) — dari LUSIN ke PCS hasilnya
            // dozen_store_price/12, bukan store_price aslinya.
            base_pcs_price: Number(r?.cost_price ?? r?.price ?? 0),
            sell_price_ref: Number(r?.sell_price_ref ?? r?.sell_price ?? 0),
          }
        })
        .sort((a, b) => String(a.product_label || "").localeCompare(String(b.product_label || ""), undefined, { sensitivity: "base" }))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, satuanOptions])

  const debouncedSetProductSearch = useMemo(
    () =>
      _.debounce((val) => {
        setProductPage(1)
        setProductSearch(val)
      }, 400),
    []
  )

  useEffect(() => {
    return () => debouncedSetProductSearch.cancel()
  }, [debouncedSetProductSearch])

  const onProductSearchChange = (val) => {
    setProductSearchValue(val)
    debouncedSetProductSearch(val)
  }

  const upperStatus = String(status || "").toUpperCase()

  // 240726 (#3): filter tampilan item berdasarkan pencarian nama/kode produk.
  const itemMatches = (it) => {
    const q = itemSearch.trim().toLowerCase()
    if (!q) return true
    return String(it.product_label || "").toLowerCase().includes(q)
      || String(it.product_unit_id || "").toLowerCase().includes(q)
  }

  const isEditable = ["DRAFT", "REJECTED"].includes(upperStatus)
  const isLocked = !isEditable
  const isProductPickBlocked = !outletId || !supplierId

  // 200726: picker multi-select — tambah banyak product unit sekaligus.
  const [showBulkPicker, setShowBulkPicker] = useState(false)
  const productSelectPlaceholder = !outletId
    ? "-- Pilih Outlet Dulu --"
    : !supplierId
      ? "-- Pilih Supplier Dulu --"
      : "-- Cari & Pilih Product Unit --"

  // reset product search when outlet or supplier changes
  useEffect(() => {
    debouncedSetProductSearch.cancel()

    setSelectedProductUnitId("")
    setSelectedProductUnit(null)
    setProductSearchValue("")
    setProductSearch("")
    setProductPage(1)
    setProductSelectKey((k) => k + 1)

    productCacheRef.current = new Map()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletId, supplierId])

  // ✅ ps_list params (only if outlet and supplier picked)
  const psListParams = useMemo(() => {
    if (!outletId || !supplierId) return null

    return {
      outlet_group_id: outletGroupId,
      outlet_id: outletId,
      supplier_id: supplierId,

      paginate: true,
      page: productPage,
      per_page: perPage,

      search: productSearch,
    }
  }, [outletId, supplierId, outletGroupId, productSearch, productPage])

  // ✅ prevent request if outlet or supplier not picked
  const rsBaseUrl = psListParams ? process.env.REACT_APP_INVENTORY_API_URL : null

  const { data: productUnitSelection, isLoading: isLoadingProducts } = useResultSet(
    rsBaseUrl,
    "ps_list",
    psListParams || {}
  )

  const productUnitRows = useMemo(() => {
    return Array.isArray(productUnitSelection?.data) ? productUnitSelection.data : []
  }, [productUnitSelection])

  useEffect(() => {
    for (const p of productUnitRows) {
      const key = String(p?.product_unit_id ?? "")
      if (key) productCacheRef.current.set(key, p)
    }
  }, [productUnitRows])

  const productMap = useMemo(() => {
    const map = new Map()
    for (const p of productUnitRows) {
      const key = String(p?.product_unit_id ?? "")
      if (key) map.set(key, p)
    }
    return map
  }, [productUnitRows])

  const totals = useMemo(() => {
    const sub = items.reduce((acc, r) => acc + num(r.qty_ordered) * num(r.cost_price), 0)
    // 200726: preview potongan diskon level-PO (server menghitung ulang di print).
    const discCut = discountType === "PERCENT"
      ? (sub * num(discountValue)) / 100
      : discountType === "AMOUNT"
        ? Math.min(num(discountValue), sub)
        : 0
    // 220726 (permintaan user): Total Qty dihitung lewat PCS riil — qty tiap
    // baris dikonversi ke pcs bulat dulu (0,17 lusin ×12 = 2,04 → 2 pcs),
    // dijumlah, lalu dibagi balik faktor satuan global. Menjumlah angka lusin
    // 2-desimal langsung menumpuk error pembulatan (6 × 0,17 = 1,02 padahal
    // riilnya 12 pcs = tepat 1 lusin).
    const totalPcs = items.reduce((acc, r) => acc + Math.round(num(r.qty_ordered) * uomFactor(r.uom)), 0)
    return {
      qtyTotal: Math.round((totalPcs / uomFactor(globalUom)) * 100) / 100,
      amountTotal: sub,
      discountCut: discCut,
      netTotal: Math.max(0, sub - discCut),
      sellPriceTotal: items.reduce((acc, r) => acc + num(r.qty_ordered) * num(r.sell_price_ref), 0),
    }
  }, [items, discountType, discountValue, globalUom])

  // HO supplier detection & real-time stock availability
  const selectedSupplier = useMemo(
    () => supplierSelection?.find((x) => String(x.id) === String(supplierId)) ?? null,
    [supplierSelection, supplierId]
  )

  const isHoSupplier = useMemo(
    () =>
      Boolean(
        selectedSupplier &&
          (selectedSupplier.is_internal === true || selectedSupplier.linked_branch_id != null)
      ),
    [selectedSupplier]
  )

  const hoOutletId = useMemo(
    () => (isHoSupplier ? String(selectedSupplier?.linked_branch_id ?? "") : null),
    [isHoSupplier, selectedSupplier]
  )

  const isHoBuyingFromSelf = useMemo(
    () => isHoSupplier && hoOutletId && String(outletId) === hoOutletId,
    [isHoSupplier, hoOutletId, outletId]
  )

  const [hoStockMap, setHoStockMap] = useState({})
  const [hoCostMap, setHoCostMap] = useState({})
  const [isLoadingHoStock, setIsLoadingHoStock] = useState(false)
  // product_unit_id yang cost-nya diedit manual → jangan ditimpa auto-isi at-cost.
  const manualCostIds = useRef(new Set())

  const productUnitIdsKey = useMemo(
    () => items.map((it) => it.product_unit_id).filter(Boolean).sort().join("|"),
    [items]
  )

  useEffect(() => {
    if (!isHoSupplier || !supplierId || items.length === 0) {
      setHoStockMap({})
      setHoCostMap({})
      return
    }

    const productUnitIds = items.map((it) => it.product_unit_id).filter(Boolean)
    if (productUnitIds.length === 0) {
      setHoStockMap({})
      setHoCostMap({})
      return
    }

    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    if (!base) return

    let cancelled = false
    setIsLoadingHoStock(true)

    axios
      // 210726: POST body (bukan GET query) — daftar id dari "Pilih Banyak" bisa
      // ratusan; via query string URL melebihi batas server (414 → tampak sbg
      // error CORS di browser) dan Stok HO jadi 0 semua.
      .post(`${base}/purchase-orders/ho-stock-availability`, {
        supplier_id: supplierId,
        product_unit_ids: productUnitIds,
        // ATALLA-255: outlet pembeli — backend memakai ini utk mengembalikan costs
        // = HARGA JUAL atalla di outlet ini (bukan weighted HPP lot Vision).
        outlet_id: outletId ?? undefined,
        // 280726: PO internal me-RESERVASI current_stock HO sejak dibuat — saat EDIT,
        // kirim po_id supaya backend menambahkan kembali reservasi milik PO ini ke
        // availability (tanpa ini qty existing dianggap melebihi stok).
        po_id: poId || id || undefined,
      })
      .then((res) => {
        if (cancelled) return
        const stocks = res?.data?.stocks ?? {}
        const costs = res?.data?.costs ?? {}
        // 210726: rantai dozen dari server (dozen outlet pembeli -> dozen HO).
        const dozenCosts = res?.data?.dozen_costs ?? {}
        setHoStockMap(stocks)
        setHoCostMap(costs)

        // Auto-isi cost dari harga jual atalla (ATALLA-255), kecuali yang diedit manual.
        setItems((prev) =>
          prev.map((it) => {
            const pid = String(it.product_unit_id)
            const atCost = Number(costs[pid])
            if (!manualCostIds.current.has(pid) && Number.isFinite(atCost) && atCost > 0) {
              // 210726: at-cost server = harga per PCS. HPP baris = at-cost × faktor uom
              // (PASANG ×2, LUSIN ×12). LUSIN pakai dozen_store_price dulu bila ada,
              // baru fallback pcs × 12. BUG lama: hanya LUSIN yang dikonversi, PASANG
              // ketiban harga pcs mentah (tanpa ×2) saat effect ini menimpa cost.
              const uomUpper = String(it.uom || "").toUpperCase()
              const factor = uomFactor(uomUpper)
              const dz = num(it.dozen_store_price) > 0 ? num(it.dozen_store_price) : num(dozenCosts[pid])
              const nextCost = isDozenUom(uomUpper)
                ? (dz > 0 ? dz : atCost * 12)
                : atCost * factor
              return {
                ...it,
                cost_price: nextCost,
                // at-cost server = harga per PCS → jadikan acuan dasar juga.
                base_pcs_price: atCost,
                dozen_store_price: dz > 0 ? dz : it.dozen_store_price,
              }
            }
            return it
          })
        )
      })
      .catch((err) => {
        if (cancelled) return
        console.error("HO stock fetch failed", err)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHoStock(false)
      })

    return () => {
      cancelled = true
    }
  }, [isHoSupplier, supplierId, productUnitIdsKey, outletId])

  const overstockMap = useMemo(() => {
    if (!isHoSupplier) return {}
    const result = {}
    for (const it of items) {
      const stock = Number(hoStockMap[it.product_unit_id] ?? 0)
      // 200726 (PCS/LUSIN): stok HO dalam PCS — qty dikonversi & DIBULATKAN
      // (210726: 0,42 lusin ×12 = 5,04 > stok 5 memicu merah palsu; pcs riil = 5).
      if (Math.round(num(it.qty_ordered) * uomFactor(it.uom)) > stock) {
        result[it.product_unit_id] = stock
      }
    }
    return result
  }, [isHoSupplier, hoStockMap, items])

  // ATALLA-310: overstock terhadap stok HO hanya PERINGATAN visual (indikator merah
  // per baris), tidak lagi memblokir simpan/submit — guard keras ada di posting GR.

  const validate = () => {
    const e = {}

    if (!outletId) e.outlet_id = "Outlet wajib dipilih"
    if (!supplierId) e.supplier_id = "Supplier wajib dipilih"
    if (!poDate) e.po_date = "Tanggal PO wajib diisi"
    if (!items.length) e.items = "Minimal 1 item"

    if (isHoBuyingFromSelf) {
      e.supplier_id = "HO tidak bisa melakukan PO ke dirinya sendiri"
    }

    for (const [idx, it] of items.entries()) {
      if (!it.product_unit_id) e[`items.${idx}.product_unit_id`] = "Product wajib diisi"
      if (num(it.qty_ordered) <= 0) e[`items.${idx}.qty_ordered`] = "Qty harus > 0"
      // if (num(it.cost_price) <= 0) e[`items.${idx}.cost_price`] = "Cost price harus > 0"
      if (!it.uom) e[`items.${idx}.uom`] = "UOM wajib diisi"

      // ATALLA-310: qty > stok HO TIDAK memblokir — toko boleh PO produk yang stok
      // HO-nya kosong (HO memenuhi belakangan). Indikator "Stok HO" per baris tetap
      // tampil sebagai peringatan; guard sesungguhnya ada saat posting GR (stok HO
      // tidak boleh minus).
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const buildPayload = (status) => ({
    outlet_id: Number(outletId),
    // 230726 (permintaan user): order_code nomor PO/dokumen = outlet USER yang
    // login (pembuat PO), bukan outlet tujuan.
    created_outlet_id: Number(currentBranch?.id) || undefined,
    supplier_id: supplierId,
    po_date: poDate,
    // Nomor PO manual (penamaan khusus HO) — kosong = digenerate sistem.
    po_number: (poNumber || "").trim() || null,
    // Estimasi barang datang — tampil di laporan Outstanding PO.
    estimate_date: estimateDate || null,
    po_type: PO_TYPE,
    po_source: poSource,
    // 200726: diskon level-PO (kosongkan tipe utk hapus diskon).
    discount_type: discountType || null,
    discount_value: discountType ? num(discountValue) : 0,
    remarks: notes || null,
    status:status,
    updatedBy: isEdit ? user?.name : null,
    // 200726 (PCS/LUSIN): server MENYIMPAN PCS — baris LUSIN dikonversi
    // (qty ×12, harga ÷12); uom tetap 'LUSIN' sebagai penanda tampilan dokumen.
    items: items.map((it) => ({
      product_unit_id: it.product_unit_id,
      uom: it.uom,
      // 210726: simpan PCS dengan presisi 3 desimal (kolom qty decimal:3) — JANGAN
      // bulatkan ke pcs utuh. Qty lusin pecahan (mis. 0,17 lusin = 2,04 pcs) harus
      // dipertahankan agar tampilan balik ÷12 = 0,17 (bukan 2→0,16666). Pembulatan
      // 3 desimal hanya untuk membuang noise float dari perkalian.
      qty_ordered: Math.round(num(it.qty_ordered) * uomFactor(it.uom) * 1000) / 1000,
      qty_received: Math.round(num(it.qty_received) * uomFactor(it.uom) * 1000) / 1000,
      cost_price: num(it.cost_price) / uomFactor(it.uom),
      sell_price_ref: num(it.sell_price_ref),
    })),
  })

  const handleSubmitLocal = (status) => {
    if (!validate()) return
    onSubmit?.(buildPayload(status))
  }

  const onAddItem = () => {
    if (!outletId) {
      toast.error("Pilih outlet dulu")
      return
    }

    if (!supplierId) {
      toast.error("Pilih supplier dulu")
      return
    }

    if (!selectedProductUnitId) {
      toast.error("Pilih product dulu")
      return
    }

    const pid = String(selectedProductUnitId)

    const p =
      selectedProductUnit ||
      productCacheRef.current.get(pid) ||
      productMap.get(pid) ||
      productUnitRows.find((x) => String(x?.product_unit_id) === pid)

    if (!p) {
      toast.error("Product tidak ditemukan (daftar berubah). Pilih ulang ya.")
      return
    }

    const product_unit_id = String(p?.product_unit_id ?? "")
    if (!product_unit_id) {
      toast.error("Product ID tidak valid")
      return
    }

    const existsIndex = items.findIndex((x) => String(x.product_unit_id) === product_unit_id)
    // 210726: item baru mengikuti satuan GLOBAL; HPP dikonversi ke satuan itu
    // (LUSIN → dozen_store_price bila ada, else harga pcs × faktor).
    const uom = globalUom
    const basePcs = num(pickCostPrice(p))
    const cost = isDozenUom(uom) && num(p?.dozen_store_price) > 0
      ? num(p.dozen_store_price)
      : basePcs * uomFactor(uom)

    if (existsIndex >= 0) {
      const next = [...items]
      next[existsIndex] = {
        ...next[existsIndex],
        qty_ordered: num(next[existsIndex].qty_ordered) + num(addQty || 1),
        uom: next[existsIndex].uom || uom,
      }
      setItems(next)
      toast.success("Qty item ditambahkan")
    } else {
      setItems([
        {
          product_unit_id,
          product_label: pickProductLabel(p),
          uom,
          qty_ordered: num(addQty || 1),
          cost_price: cost,
          base_pcs_price: basePcs,
          // 210726: harga lusin outlet (utk satuan LUSIN: dozen_store_price dulu,
          // fallback store_price × 12 — lihat handler ganti satuan).
          dozen_store_price: num(p?.dozen_store_price ?? 0),
          price_active_date: p?.price_active_date ?? p?.active_date ?? null,
          sell_price_ref: num(p?.sell_price_ref ?? 0),
        },
        ...items,
      ])
      toast.success("Item ditambahkan")
    }

    debouncedSetProductSearch.cancel()
    setSelectedProductUnitId("")
    setSelectedProductUnit(null)
    setAddQty("")
    setProductSearchValue("")
    setProductSearch("")
    setProductPage(1)
    setProductSelectKey((k) => k + 1)
  }

  // 200726: tambah banyak item sekaligus dari ProductPickerModal. Merge per unit
  // mengikuti perilaku onAddItem (unit sudah ada → qty dijumlahkan, tidak dobel baris).
  const onAddItemsBulk = (entries) => {
    if (!entries?.length) return

    setItems((prev) => {
      let next = [...prev]
      for (const { product: p, qty } of entries) {
        const pid = String(p?.product_unit_id ?? "")
        if (!pid) continue

        productCacheRef.current.set(pid, p)
        const q = num(qty) > 0 ? num(qty) : 1
        const existsIndex = next.findIndex((x) => String(x.product_unit_id) === pid)

        if (existsIndex >= 0) {
          next[existsIndex] = {
            ...next[existsIndex],
            qty_ordered: num(next[existsIndex].qty_ordered) + q,
            uom: globalUom,
          }
        } else {
          next = [
            {
              product_unit_id: pid,
              product_label: pickProductLabel(p),
              // 210726: ikut satuan GLOBAL + HPP dikonversi ke satuan itu.
              uom: globalUom,
              qty_ordered: q,
              cost_price: isDozenUom(globalUom) && num(p?.dozen_store_price) > 0
                ? num(p.dozen_store_price)
                : num(pickCostPrice(p)) * uomFactor(globalUom),
              base_pcs_price: num(pickCostPrice(p)),
              // 210726: utk satuan LUSIN — dozen_store_price dulu, fallback pcs × 12.
              dozen_store_price: num(p?.dozen_store_price ?? 0),
              price_active_date: p?.price_active_date ?? p?.active_date ?? null,
              sell_price_ref: num(p?.sell_price_ref ?? 0),
            },
            ...next,
          ]
        }
      }
      return next
    })

    toast.success(`${entries.length} item ditambahkan`)
    setShowBulkPicker(false)
  }

  const onRemoveItem = (product_unit_id) => {
    setItems(items.filter((x) => String(x.product_unit_id) !== String(product_unit_id)))
  }

  const onUpdateItem = (product_unit_id, patch) => {
    setItems((prev) =>
      prev.map((it) =>
        String(it.product_unit_id) === String(product_unit_id) ? { ...it, ...patch } : it
      )
    )
  }

  const onApprove = async () => {
    // catatan: approve/cancel ini butuh endpoint PO inventory.
    // kalau di parent kamu yang handle, pindahin ini ke parent juga.
    if (!poId && !id) {
      toast.error("Simpan PO dulu sebelum approve")
      return
    }
    if (!window.confirm(`Approve PO ${poNumber || ""}?`)) return
    try {
      nProgress.start()
      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/approve`
      console.log(user)
      const res = await fetch(url, { method: "POST",body:JSON.stringify({ approvedBy:user?.name }), headers: { "Content-Type": "application/json" } })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal approve PO")
      toast.success(json?.message || "PO berhasil di-approve")
      setStatus("APPROVED")
    } catch (e) {
      toast.error(e.message || "Gagal approve PO")
    } finally {
      nProgress.done()
    }
  }

  const onCancel = async () => {
    if (!poId && !id) return
    const reason = window.prompt("Alasan cancel (opsional):", "")
    if (reason === null) return
    try {
      nProgress.start()
      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/cancel`
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: reason ? JSON.stringify({ reason,cancelledBy:user?.name }) : null,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal cancel PO")
      toast.success(json?.message || "PO berhasil dibatalkan")
      setStatus("CANCELLED")
    } catch (e) {
      toast.error(e.message || "Gagal cancel PO")
    } finally {
      nProgress.done()
    }
  }

  const onReject = async () => {
    if (!poId && !id) {
      toast.error("Simpan PO dulu sebelum reject")
      return
    }

    const reason = window.prompt("Alasan reject (wajib diisi):", "")
    if (reason === null) return

    if (!reason.trim()) {
      toast.error("Alasan reject wajib diisi")
      return
    }

    if (!window.confirm(`Reject PO ${poNumber || ""}?`)) return


    try {
      nProgress.start()

      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/reject`

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason,rejectedBy:user?.name }),
      })

      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal reject PO")

      toast.success(json?.message || "PO berhasil di-reject")
      setStatus("REJECTED")
      // ATALLA-316: PO yang sudah di-reject tidak bisa diapa-apakan lagi di halaman
      // edit — langsung arahkan kembali ke daftar PO.
      navigate("/purchase", { state: location.state })
    } catch (e) {
      toast.error(e.message || "Gagal reject PO")
    } finally {
      nProgress.done()
    }
  }

  const onClose = async () => {
    if (!poId && !id) {
      toast.error("PO tidak ditemukan")
      return
    }

    if (!window.confirm(`Close PO ${poNumber || ""}?`)) return

    try {
      nProgress.start()

      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/close`

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          closedBy: user?.name,
        }),
      })

      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal close PO")

      toast.success(json?.message || "PO berhasil di-close")
      setStatus("CLOSED")
    } catch (e) {
      toast.error(e.message || "Gagal close PO")
    } finally {
      nProgress.done()
    }
  }

  const canClose =
  isEdit &&
  can?.("purchase-order.close") &&
  ["PARTIAL_RECEIVED", "RECEIVED"].includes(upperStatus)

  // 240726 (#5 permintaan user): tutup SISA item PO terpilih (checklist) — hilang dari
  // laporan qty setahun (kolom Po). Berlaku utk PO APPROVED / PARTIAL_RECEIVED.
  const canCloseItems = ["APPROVED", "PARTIAL_RECEIVED"].includes(upperStatus) && data?.po_type === "REGULAR"
  const [selectedCloseIds, setSelectedCloseIds] = useState(() => new Set())
  const [closingItems, setClosingItems] = useState(false)

  const toggleCloseId = (itemId) => {
    setSelectedCloseIds((prev) => {
      const next = new Set(prev)
      next.has(itemId) ? next.delete(itemId) : next.add(itemId)
      return next
    })
  }

  const onCloseSelectedItems = async () => {
    const ids = Array.from(selectedCloseIds)
    if (!ids.length) return
    if (!window.confirm(`Tutup ${ids.length} sisa item? Sisa qty item ini tidak akan muncul lagi di laporan qty setahun.`)) return
    try {
      setClosingItems(true)
      nProgress.start()
      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const res = await fetch(`${base}/purchase-orders/${poId || id}/close-items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_ids: ids, note: null }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal menutup item")
      // Tandai item yang ditutup di state lokal + bersihkan pilihan.
      setItems((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, closed: true } : it)))
      setSelectedCloseIds(new Set())
      if (json?.data?.po_closed) setStatus("CLOSED")
      toast.success(json?.message || "Sisa item ditutup")
    } catch (e) {
      toast.error(e.message || "Gagal menutup item")
    } finally {
      setClosingItems(false)
      nProgress.done()
    }
  }

  // 270726 (permintaan user): Faktur & Surat Jalan bisa dicetak sejak PO di-SUBMIT
  // (bukan lagi setelah approve); print DO boleh sejak DRAFT (dokumen picking
  // gudang + cek stok HO) — menu tampil utk DRAFT berisi DO saja (gating per-item
  // di PurchaseExportActions via poStatus).
  const canExport = isEdit && ["DRAFT", "SUBMITTED", "APPROVED", "PARTIAL_RECEIVED", "RECEIVED", "CLOSED"].includes(upperStatus)

  const canCancel = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return !["CANCELLED", "RECEIVED", "PARTIAL_RECEIVED"].includes(st)
  }

  // ── Print PDF (client-side via react-to-print) ───────────────────────────
  const printRef = useRef()
  const handlePrint = useReactToPrint({
    documentTitle: `Purchase Order ${poNumber || id || ""}`.trim(),
    removeAfterPrint: true,
  })
  const onPrint = () => {
    if (!items.length) return toast.error("Tidak ada item untuk dicetak")
    handlePrint(null, () => printRef.current)
  }


  return (
    <div className="pb-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">
            {isEdit ? "Edit Purchase Order" : "Buat Purchase Order"}{" "}
            <span className="text-xs font-medium text-neutral-500">({ data?.po_type || PO_TYPE})</span>
          </h1>
          <p className="text-xs text-neutral-500">
            Konsep baru PO: header + items, approve/cancel, lalu goods receipt.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {canExport && (
            <PurchaseExportActions poId={poId || id} poStatus={status} doPrinted={!!poDoNo} />
          )}

          {isEdit && (
            <button
              type="button"
              onClick={onPrint}
              className="inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold border text-neutral-700 border-neutral-200 rounded-xl hover:bg-neutral-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-neutral-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9V2h12v7" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <path d="M6 14h12v8H6z" />
              </svg>
              Print PDF
            </button>
          )}

          <Link
            to="/purchase"
            className="px-4 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
            state={{ back: title, from: location?.pathname, transition: "slide" }}
          >
            Back
          </Link>

          {can?.("purchase-order.cancel") && isEdit && status !== "CANCELLED" && status !== "REJECTED" && status !== 'CLOSED' && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-3 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-500"
            >
              Cancel
            </button>
          )}

          {can?.("purchase-order.reject") &&
            isEdit &&
            String(status).toUpperCase() === "SUBMITTED" && (
              <button
                type="button"
                onClick={onReject}
                className="px-4 py-3 text-xs font-semibold text-white bg-orange-600 rounded-xl hover:bg-brand-500"
              >
                Reject
              </button>
          )}

          {can?.("purchase-order.approve") && isEdit && String(status).toUpperCase() === "SUBMITTED" && (
            <button
              type="button"
              onClick={onApprove}
              className="px-4 py-3 text-xs font-semibold text-white bg-green-600 rounded-xl hover:bg-green-500"
            >
              Approve
            </button>
          )}

          {canClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 text-xs font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-500"
            >
              Close
            </button>
          )}


          {(can?.("purchase-order.update")) && (
            <button
              type="button"
              onClick={(e) => handleSubmitLocal('DRAFT')}
              disabled={(isLocked && isEdit) || isHoBuyingFromSelf || submitting}
              className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                (isLocked && isEdit) || isHoBuyingFromSelf || submitting
                  ? "bg-neutral-400 cursor-not-allowed"
                  : "bg-brand-900 hover:bg-brand-800"
              }`}
            >
              Simpan Draft
            </button>
          )}

          {can?.("purchase-order.create") &&
            ["DRAFT", "REJECTED"].includes(String(status).toUpperCase()) && (
            <button
              type="button"
              onClick={(e) => handleSubmitLocal('SUBMITTED')}
              disabled={isHoBuyingFromSelf || submitting}
              className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                isHoBuyingFromSelf || submitting
                  ? "bg-neutral-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-500"
              }`}
            >
              Submit
            </button>
          )}
        </div>
      </div>

      {/* Main Form */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-neutral-700">{lang.purchase_number}</label>
              {/* Editable (penamaan khusus HO). Kosongkan = nomor otomatis dari sistem. */}
              <Input
                disabled={isEdit && isLocked}
                value={poNumber || ""}
                placeholder="Kosongkan untuk nomor otomatis"
                onChange={(e) => setPoNumber(e.target.value)}
              />
              {errors?.po_number && (
                <div className="mt-1 text-[11px] text-red-600">{errors.po_number}</div>
              )}
              {/* 240726 (permintaan user): nomor DO yang sudah ter-generate. */}
              {poDoNo && (
                <div className="mt-1 text-[11px] text-neutral-500">No. DO: <span className="font-medium text-neutral-700">{poDoNo}</span></div>
              )}
            </div>

            <div>
              <label className="block text-xs text-neutral-700">{lang.status}</label>
              <Input disabled value={status || "DRAFT"} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.outlet_name}<sup className="text-xs text-red-700"> *</sup>
              </label>
              {canPickOutlet ? (
                <SelectSearchable
                  disabled={isEdit && isLocked}
                  filter={(value) => value.name}
                  onChange={(value) => {
                    const changed = String(value) !== String(outletId)
                    const b = outletOptions?.find((x) => String(x.id) === String(value))
                    setOutletId(value)
                    setOutletName(b?.name ?? null)
                    // Ganti outlet → reset supplier (item picker sudah di-reset oleh
                    // useEffect [outletId, supplierId]) supaya ps_list re-query per outlet.
                    setSupplierId(null)
                    setSupplierName(null)
                    // 210726 (revisi keputusan 170726): item TIDAK lagi dikosongkan saat
                    // outlet berganti — daftar dipertahankan. Utk supplier internal, cost
                    // otomatis di-sync ulang ke outlet baru (effect ho-stock-availability
                    // ber-dependency outletId); harga supplier eksternal tetap angka lama.
                    if (changed && items.length > 0) {
                      toast("Outlet berubah — item dipertahankan. Cek kembali harga per item bila perlu.", { icon: "ℹ️" })
                    }
                  }}
                  selection={outletOptions}
                  isLoading={false}
                  value={outletId}
                  keyValue={(value) => value.id}
                  title={(value) => value.name}
                  placeholder={`-- Pilih Outlet --`}
                />
              ) : (
                // User toko biasa: outlet dikunci ke branch sendiri (read-only).
                <Input disabled value={outletName || currentBranch?.name || "-"} />
              )}
              <ErrorMessage error={errors.outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.supplier_name}<sup className="text-xs text-red-700"> *</sup>
                {isHoSupplier && (
                  <span className="ml-2 inline-block px-2 py-0.5 text-[10px] font-semibold text-orange-700 bg-orange-100 rounded">
                    HO (Head Office)
                  </span>
                )}
              </label>
              <SelectSearchable
                disabled={isEdit && isLocked}
                filter={(value) => value.name}
                onChange={(value) => {
                  setSupplierId(value)
                  const s = supplierSelection?.find((x) => String(x.id) === String(value))
                  setSupplierName(s?.name ?? null)
                }}
                selection={filteredSupplierSelection}
                isLoading={false}
                value={supplierId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder={`-- Pilih Supplier --`}
              />
              <ErrorMessage error={errors.supplier_id} />
              {isHoSupplier && (
                <div className="mt-1 text-[11px] text-orange-700">
                  Supplier ini adalah HO — stok HO akan berkurang saat goods receipt diposting.
                </div>
              )}
              {isHoBuyingFromSelf && (
                <div className="mt-1 text-[11px] text-red-600">
                  HO tidak bisa melakukan PO ke dirinya sendiri.
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                Tanggal PO<sup className="text-xs text-red-700"> *</sup>
              </label>
              <input
                type="date"
                disabled={isEdit && isLocked}
                value={poDate}
                onChange={(e) => setPoDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
              <ErrorMessage error={errors.po_date} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Tanggal Estimasi Datang</label>
              <input
                type="date"
                disabled={isEdit && isLocked}
                value={estimateDate}
                onChange={(e) => setEstimateDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
              <p className="mt-1 text-[11px] text-neutral-500">Opsional — tampil di laporan Outstanding PO.</p>
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Tipe PO</label>
              <Input disabled value={data?.po_type || PO_TYPE} />
            </div>

            {/* Pilihan "Sumber PO" dihilangkan (permintaan user). po_source tetap dikirim
                dari state (default FACTORY; nilai existing dipertahankan saat edit). */}

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.notes}</label>
              <textarea
                disabled={isEdit && isLocked}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
              <ErrorMessage error={errors.notes} />
            </div>
          </div>
        </div>
      </div>

      {/* Add Item */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Items</div>
              <div className="text-xs text-neutral-500">
                Product units.
                Total Qty: <b className="text-lg text-orange-500">{qtyFmt(totals.qtyTotal)}</b>
              </div>
            </div>
            <div>
            </div>
            <div className="flex items-end gap-2">
              {/* 200726: diskon level-PO */}
              <div>
                <label className="block text-[10px] text-neutral-500">Diskon PO</label>
                <select
                  value={discountType}
                  disabled={isEdit && isLocked}
                  onChange={(e) => setDiscountType(e.target.value)}
                  className="px-2 py-2 text-xs border border-neutral-200 rounded-lg disabled:bg-neutral-200"
                >
                  <option value="">Tanpa Diskon</option>
                  <option value="PERCENT">Persen (%)</option>
                  <option value="AMOUNT">Nominal (Rp)</option>
                </select>
              </div>
              {discountType && (
                <input
                  type="number"
                  min={0}
                  value={discountValue}
                  disabled={isEdit && isLocked}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === "PERCENT" ? "%" : "Rp"}
                  className="w-28 px-2 py-2 text-xs border border-neutral-200 rounded-lg disabled:bg-neutral-200"
                />
              )}
            </div>
            <div className="text-xs text-neutral-600">
              Total HPP: <b>{moneyFmt(totals.amountTotal)}</b>
              {totals.discountCut > 0 && (
                <div className="text-rose-600">
                  Diskon{discountType === "PERCENT" ? ` ${num(discountValue)}%` : ""}: - {moneyFmt(totals.discountCut)}
                  <span className="ml-2 text-neutral-800">Total: <b>{moneyFmt(totals.netTotal)}</b></span>
                </div>
              )}
            <div className="text-xs text-neutral-600">
              Total Sell Price: <b>{moneyFmt(totals.sellPriceTotal)}</b>
            </div>
            </div>
          </div>
          <ErrorMessage error={errors.items} />

          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-6">
              <label className="block text-xs text-neutral-700">Produk</label>

              <SelectSearchable
                key={`po-product-select-${productSelectKey}`}
                disabled={(isEdit && isLocked) || isProductPickBlocked}
                selection={productUnitRows}
                isLoading={isLoadingProducts}
                value={selectedProductUnitId}
                onChange={(value, row) => {
                  const pid = String(value ?? "")
                  setSelectedProductUnitId(pid)

                  const found =
                    row ||
                    productUnitRows.find((p) => String(p?.product_unit_id) === pid) ||
                    productCacheRef.current.get(pid) ||
                    null

                  if (pid && found) {
                    productCacheRef.current.set(pid, found)
                  }

                  setSelectedProductUnit(found)
                }}
                keyValue={(value) => String(value?.product_unit_id ?? "")}
                title={(value) =>
                  value?.product_unit_long_name ||
                  value?.product_unit ||
                  value?.sku ||
                  value?.product_model ||
                  value?.product_unit_id ||
                  "-"
                }
                description={(value) => {
                  const parts = [
                    value?.product_model,
                    value?.sku ? `SKU: ${value.sku}` : null,
                    value?.product_series,
                    value?.product_brand,
                    value?.product_type,
                    // 200726 (permintaan user): tampilkan stok di hasil pencarian produk.
                    // ps_list sudah mengembalikan current_stock (stok HO utk supplier internal,
                    // stok outlet pembeli utk supplier eksternal).
                    value?.current_stock !== undefined && value?.current_stock !== null
                      ? `Stok: ${Number(value.current_stock).toLocaleString("id-ID")}`
                      : null,
                  ].filter(Boolean)
                  return parts.join(" • ")
                }}
                placeholder={productSelectPlaceholder}
                reverse
                serverSearch
                searchValue={productSearchValue}
                onSearchChange={onProductSearchChange}
                page={productPage}
                onNextPage={() => {
                  if (productUnitSelection?.meta?.next_page_url) setProductPage((p) => p + 1)
                }}
                onPrevPage={() => {
                  if (productUnitSelection?.meta?.prev_page_url) setProductPage((p) => Math.max(1, p - 1))
                }}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">Qty</label>
              <input
                type="number"
                min={1}
                disabled={isEdit && isLocked}
                value={addQty}
                onChange={(e) => setAddQty(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-2">
              <button
                type="button"
                disabled={(isEdit && isLocked) || isProductPickBlocked}
                onClick={onAddItem}
                className={`w-full inline-flex justify-center items-center gap-2 px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  (isEdit && isLocked) || isProductPickBlocked ? "bg-neutral-400 cursor-not-allowed" : "bg-brand-900 hover:bg-brand-800"
                }`}
              >
                <Plus className="w-4 h-4" stroke={1.5} />
                Add
              </button>
            </div>

            <div className="col-span-2">
              <button
                type="button"
                disabled={(isEdit && isLocked) || isProductPickBlocked}
                onClick={() => {
                  if (!outletId) return toast.error("Pilih outlet dulu")
                  if (!supplierId) return toast.error("Pilih supplier dulu")
                  setShowBulkPicker(true)
                }}
                className={`w-full inline-flex justify-center items-center gap-2 px-4 py-3 text-xs font-semibold rounded-xl border ${
                  (isEdit && isLocked) || isProductPickBlocked
                    ? "border-neutral-200 text-neutral-400 cursor-not-allowed"
                    : "border-brand-900 text-neutral-900 hover:bg-neutral-100"
                }`}
              >
                Pilih Banyak
              </button>
            </div>
          </div>

          {/* Mount kondisional: hook fetch di modal hanya hidup saat terbuka. */}
          {showBulkPicker && (
            <ProductPickerModal
              open
              onClose={() => setShowBulkPicker(false)}
              onAdd={onAddItemsBulk}
              outletId={outletId}
              supplierId={supplierId}
              outletGroupId={outletGroupId}
              uom={globalUom}
            />
          )}

          {/* 210726: satuan GLOBAL untuk seluruh item PO (bukan per baris). */}
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs font-medium text-neutral-600">Satuan (semua item)</label>
            <select
              value={globalUom}
              disabled={isEdit && isLocked}
              onChange={(e) => {
                const nextUom = e.target.value
                setItems((prev) =>
                  prev.map((it) => {
                    // 290726 (laporan user): konversi SELALU dari harga DASAR per PCS
                    // (base_pcs_price = store_price / at-cost server), bukan dari harga
                    // satuan sebelumnya. Tanpa ini, LUSIN → PCS menghasilkan
                    // dozen_store_price / 12 dan bukan store_price aslinya.
                    // Satuan ber-faktor 12 tetap memakai dozen_store_price bila ada.
                    const prevF = uomFactor(it.uom)
                    const nextF = uomFactor(nextUom)
                    const basePcs = num(it.base_pcs_price) > 0
                      ? num(it.base_pcs_price)
                      : num(it.cost_price) / prevF   // item lama di state: turunkan sekali
                    let nextCost = basePcs * nextF
                    if (isDozenUom(nextUom) && num(it.dozen_store_price) > 0) {
                      nextCost = num(it.dozen_store_price)
                    }
                    return { ...it, uom: nextUom, cost_price: nextCost, base_pcs_price: basePcs }
                  })
                )
                setGlobalUom(nextUom)
              }}
              className="w-32 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
            >
              {/* 230726: opsi dari master satuan; satuan PO lama (mis. LUSIN) yang
                  tak ada di master tetap diinjeksi supaya value select cocok. */}
              {((satuanOptions ?? UOM_OPTIONS).includes(globalUom)
                ? (satuanOptions ?? UOM_OPTIONS)
                : [globalUom, ...(satuanOptions ?? UOM_OPTIONS)]
              ).map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          {/* 240726 (#3 permintaan user): cari item PO (nama/kode) — 1 PO bisa banyak produk. */}
          <div className="mb-3">
            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Cari item (nama / kode produk)…"
              className="w-full px-3 py-2 text-xs border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800 sm:w-80"
            />
            {itemSearch.trim() && (
              <span className="ml-2 text-[11px] text-neutral-500">
                {items.filter(itemMatches).length} dari {items.length} item
              </span>
            )}
          </div>

          {/* 240726 (#5): tombol tutup sisa item terpilih (APPROVED / PARTIAL_RECEIVED). */}
          {canCloseItems && (
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={onCloseSelectedItems}
                disabled={closingItems || selectedCloseIds.size === 0}
                className={`px-3 py-2 text-[11px] font-semibold rounded-lg text-white ${
                  closingItems || selectedCloseIds.size === 0
                    ? "bg-neutral-300 cursor-not-allowed"
                    : "bg-amber-600 hover:bg-amber-500"
                }`}
              >
                {closingItems ? "Menutup…" : `Tutup Sisa Item Terpilih${selectedCloseIds.size ? ` (${selectedCloseIds.size})` : ""}`}
              </button>
              <span className="text-[11px] text-neutral-500">
                Sisa item yang ditutup tidak akan muncul lagi di laporan qty setahun. Bila semua sisa ditutup, PO otomatis CLOSED.
              </span>
            </div>
          )}

          <div className="overflow-x-auto border border-neutral-200 rounded-xl">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Produk</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty</th>
                  {/* 230726 (permintaan user): Qty Diterima juga tampil utk RECEIVED / PARTIAL_RECEIVED. */}
                  {['CLOSED', 'RECEIVED', 'PARTIAL_RECEIVED'].includes(status) && data?.po_type === 'REGULAR' && <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty Diterima</th>}
                  {/* 240726 (#2 permintaan user): sisa qty (dipesan − diterima) utk PO PARTIAL_RECEIVED. */}
                  {status === 'PARTIAL_RECEIVED' && data?.po_type === 'REGULAR' && <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Sisa</th>}
                  {/* 240726 (#5 permintaan user): kolom tutup sisa item (APPROVED / PARTIAL_RECEIVED). */}
                  {canCloseItems && <th className="px-3 py-3 text-xs font-medium text-center uppercase text-neutral-500">Tutup</th>}
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">HPP</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Total HPP</th>
                  {status === 'CLOSED' && data?.po_type === 'CONSIGNMENT' && <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Sell Price</th>}
                  {status === 'CLOSED' && data?.po_type === 'CONSIGNMENT' && <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Total Sell Price</th>}
                  {status !== 'CLOSED' && <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Aksi</th>}
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-neutral-200">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-xs text-center text-neutral-500">
                      Belum ada item
                    </td>
                  </tr>
                )}

                {items.map((it, idx) => {
                  // 240726 (#3): sembunyikan baris yang tak cocok pencarian (idx tetap asli).
                  if (!itemMatches(it)) return null
                  const lineTotal = num(it.qty_ordered) * num(it.cost_price)
                  const sisaQty = num(it.qty_ordered) - num(it.qty_received)
                  return (
                    <tr key={it.product_unit_id} className="hover:bg-brand-50 transition-colors">
                      <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                        {it.product_label || it.product_unit_id}
                        <div className="text-[11px] text-neutral-500">{it.product_unit_id}</div>

                        {errors[`items.${idx}.product_unit_id`] && (
                          <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.product_unit_id`]}</div>
                        )}
                      </td>


                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={1}
                          value={it.qty_ordered}
                          disabled={isEdit && isLocked}
                          onChange={(e) => onUpdateItem(it.product_unit_id, { qty_ordered: e.target.value })}
                          onBlur={(e) => onUpdateItem(it.product_unit_id, { qty_ordered: qtyInputStr(e.target.value) })}
                          className={`w-24 px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200 ${
                            isHoSupplier && overstockMap[it.product_unit_id] != null
                              ? "border-red-300"
                              : "border-neutral-200"
                          }`}
                        />
                        {isHoSupplier && (
                          <div
                            className={`mt-1 text-[11px] ${
                              overstockMap[it.product_unit_id] != null
                                ? "text-red-600 font-medium"
                                : "text-neutral-500"
                            }`}
                          >
                            Stok HO: {isLoadingHoStock ? "…" : Number(hoStockMap[it.product_unit_id] ?? 0)}
                          </div>
                        )}
                        {errors[`items.${idx}.qty_ordered`] && (
                          <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.qty_ordered`]}</div>
                        )}
                      </td>

                      {['CLOSED', 'RECEIVED', 'PARTIAL_RECEIVED'].includes(status) && data?.po_type === 'REGULAR' && <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={1}
                          value={it.qty_received}
                          disabled={true}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                      </td>}

                      {/* 240726 (#2): sisa qty belum diterima (dipesan − diterima), satuan PO. */}
                      {status === 'PARTIAL_RECEIVED' && data?.po_type === 'REGULAR' && <td className="px-3 py-4 text-xs whitespace-nowrap">
                        <span className={`font-semibold ${sisaQty > 0 ? "text-amber-600" : "text-neutral-400"}`}>
                          {sisaQty.toLocaleString("id-ID", { maximumFractionDigits: 2 })}
                        </span>
                      </td>}

                      {/* 240726 (#5): checklist tutup sisa item. Sudah ditutup → badge; sisa 0 → strip. */}
                      {canCloseItems && <td className="px-3 py-4 text-xs text-center whitespace-nowrap">
                        {it.closed ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full bg-neutral-200 text-neutral-600">Ditutup</span>
                        ) : sisaQty > 0 ? (
                          <input
                            type="checkbox"
                            checked={selectedCloseIds.has(it.id)}
                            onChange={() => toggleCloseId(it.id)}
                            disabled={closingItems}
                            className="w-4 h-4 rounded border-neutral-300 text-amber-600 focus:ring-amber-500"
                            title="Tutup sisa item ini"
                          />
                        ) : (
                          <span className="text-neutral-300">—</span>
                        )}
                      </td>}

                      {status !== 'CLOSED' ? <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={0}
                          disabled={!isHoSupplier || (isEdit && isLocked)}
                          value={it.cost_price}
                          onChange={(e) => {
                            if (!isHoSupplier) return
                            const v = e.target.value
                            const pid = String(it.product_unit_id)
                            manualCostIds.current.add(pid)
                            setItems((prev) =>
                              prev.map((x) =>
                                String(x.product_unit_id) === pid
                                  ? {
                                      ...x,
                                      cost_price: v,
                                      // harga diketik dalam satuan aktif → simpan dasarnya per PCS
                                      base_pcs_price: num(v) / uomFactor(x.uom),
                                    }
                                  : x
                              )
                            )
                          }}
                          className="w-36 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                        {/* <div className="mt-1 text-[11px] text-neutral-500">
                          {isHoSupplier
                            ? "At-cost HPP Vision (bisa diubah)"
                            : `Harga aktif${it.price_active_date ? `: ${it.price_active_date}` : ""}`}
                        </div> */}
                        {errors[`items.${idx}.cost_price`] && (
                          <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.cost_price`]}</div>
                        )}
                      </td> : <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(it.cost_price)}</td>}

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(lineTotal)}</td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(it.sell_price_ref)}</td>
                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(it.sell_price_ref * it.qty_ordered)}</td>

                      {status !== 'CLOSED' && <td className="px-3 py-4 text-xs text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={isEdit && isLocked}
                          onClick={() => onRemoveItem(it.product_unit_id)}
                          className={`px-3 py-2 text-[11px] font-semibold text-white rounded-lg ${
                            isEdit && isLocked ? "bg-neutral-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-500"
                          }`}
                        >
                          Hapus
                        </button>
                      </td>}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PurchaseOrderPrintDocument
        ref={printRef}
        poNumber={poNumber}
        status={status}
        poType={data?.po_type || PO_TYPE}
        outletName={outletName}
        supplierName={supplierName}
        poDate={poDate}
        notes={notes}
        items={items}
        totalQty={totals.qtyTotal}
        totalAmount={totals.amountTotal}
        sellPriceTotal={totals.sellPriceTotal}
        printedBy={user?.name}
      />
    </div>
  )
}
